import { isEqual } from 'lodash'
import { MutableRefObject, RefObject, useEffect, useMemo } from 'react'
import {
  createExistingFileRecord,
  FileRecord,
  ResourcePrefix,
  useFileUploadContext,
} from '../controller/UploadFileContext'
import { isBlobUrl } from '../utils'

/**
 * Helper to extract blob URL from a value (string or record with file_contents)
 */
const extractBlobUrl = (item: any): string | undefined => {
  if (typeof item === 'string' && isBlobUrl(item)) {
    return item
  }
  if (typeof item === 'object' && item !== null && 'file_contents' in item) {
    const fileContents = item.file_contents
    if (typeof fileContents === 'string' && isBlobUrl(fileContents)) {
      return fileContents
    }
  }
  return undefined
}

export const useSetFileRecordsFromExternal = (
  value: any,
  multiple: boolean,
  slot: string,
  resource_prefix: ResourcePrefix,
  previousValueRef: MutableRefObject<any>,
  isInternalUpdateRef?: RefObject<boolean>
) => {
  const { fileRecords, setFileRecords, uploadStatus } = useFileUploadContext()

  // Normalize value to array format for consistent processing
  const normalizedValue = useMemo(() => {
    if (!value) {
      return []
    }
    return multiple || Array.isArray(value) ? value : multiple ? [] : [value]
  }, [value, multiple])

  // Convert normalized values to FileRecords
  const filesFromValue = useMemo(() => {
    if (normalizedValue.length === 0) {
      return []
    }

    return normalizedValue
      .map((item) => {
        try {
          return createExistingFileRecord(item, slot)
        } catch (e) {
          return null
        }
      })
      .filter((r): r is FileRecord => r !== null)
  }, [normalizedValue, slot])

  // Sync filesFromValue to context (when form field value changes externally)
  useEffect(() => {
    // Step 1: Single check for whether to process - skip if internal update, uploading, or value unchanged
    if (isInternalUpdateRef?.current || uploadStatus === 'uploading' || isEqual(previousValueRef.current, value)) {
      previousValueRef.current = value
      return
    }

    // Step 2: Convert values to FileRecords and enrich from context
    const currentSourceRecords = fileRecords.filter((r) => r.slot === slot)
    const currentPendingRecords = currentSourceRecords.filter((r) => r.status === 'pending')
    const currentUploadedRecords = currentSourceRecords.filter((r) => r.status === 'uploaded')

    // Enrich filesFromValue with data from context (both uploaded and pending records)
    const enrichedFilesFromValue = filesFromValue.map((fileRecord) => {
      // Check if there's a matching uploaded record in context
      const matchingUploadedRecord = currentUploadedRecords.find(
        (r) =>
          fileRecord.url === r.fileRecord?.file_contents ||
          fileRecord.url === r.fileRecord?.url ||
          fileRecord.id === r.id
      )

      if (matchingUploadedRecord && matchingUploadedRecord.fileRecord) {
        // Enrich with data from uploaded record
        return {
          ...fileRecord,
          fileRecord: matchingUploadedRecord.fileRecord,
          url: matchingUploadedRecord.fileRecord?.file_contents || fileRecord.url,
          name: matchingUploadedRecord.fileRecord?.title || fileRecord.name,
          file: matchingUploadedRecord.file || fileRecord.file,
        }
      }

      // Check if there's a matching pending record (for blob URLs)
      const blobUrl = extractBlobUrl(fileRecord.url || fileRecord.existingValue)
      if (blobUrl) {
        const matchingPendingRecord = currentPendingRecords.find((r) => r.url === blobUrl)
        if (matchingPendingRecord) {
          // Enrich with data from pending record
          return {
            ...fileRecord,
            url: matchingPendingRecord.url,
            name: matchingPendingRecord.name,
            file: matchingPendingRecord.file,
            status: 'pending' as const,
            uploadStatus: matchingPendingRecord.uploadStatus,
          }
        }
      }

      return fileRecord
    })

    // Step 3: Build new records - keep pending/uploaded that aren't matched, add enriched filesFromValue
    // Track which pending/uploaded records were matched by filesFromValue
    const matchedPendingOrUploadedIds = new Set<string | number>()
    enrichedFilesFromValue.forEach((f) => {
      // Check if this enriched record matches a pending record
      const blobUrl = extractBlobUrl(f.url || f.existingValue)
      if (blobUrl) {
        const matchingPending = currentPendingRecords.find((r) => r.url === blobUrl)
        if (matchingPending) {
          matchedPendingOrUploadedIds.add(matchingPending.url || '')
        }
      }
      // Check if this enriched record matches an uploaded record
      const matchingUploaded = currentUploadedRecords.find(
        (r) => f.url === r.fileRecord?.file_contents || f.url === r.fileRecord?.url || f.id === r.id
      )
      if (matchingUploaded) {
        matchedPendingOrUploadedIds.add(matchingUploaded.id || matchingUploaded.url || '')
      }
    })

    // Keep pending/uploaded records that weren't matched by filesFromValue
    const unmatchedPendingOrUploaded = currentSourceRecords.filter(
      (r) =>
        (r.status === 'pending' || r.status === 'uploaded') && !matchedPendingOrUploadedIds.has(r.id || r.url || '')
    )

    // Build final records: unmatched pending/uploaded + enriched filesFromValue
    const newSlotRecords = [...unmatchedPendingOrUploaded, ...enrichedFilesFromValue]

    // Step 4: Only commit changes if there are actual differences
    if (!isEqual(currentSourceRecords, newSlotRecords)) {
      setFileRecords(slot, newSlotRecords, resource_prefix)
    }

    previousValueRef.current = value
  }, [filesFromValue, value, slot, resource_prefix, fileRecords, setFileRecords, uploadStatus, isInternalUpdateRef])
}
